import React, { createContext, useContext, useState, useEffect } from 'react';

export type CanvasMode = 'composer' | 'online';

interface CanvasModeContextType {
  mode: CanvasMode;
  setMode: (mode: CanvasMode) => void;
  isComposerMode: boolean;
  isOnlineMode: boolean;
}

const CanvasModeContext = createContext<CanvasModeContextType | undefined>(undefined);

export const CanvasModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<CanvasMode>(() => {
    // Load saved mode from localStorage
    const saved = localStorage.getItem('canvas-mode');
    return (saved === 'composer' || saved === 'online') ? saved : 'online';
  });

  // Save mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('canvas-mode', mode);
  }, [mode]);

  const setMode = (newMode: CanvasMode) => {
    setModeState(newMode);
  };

  const value: CanvasModeContextType = {
    mode,
    setMode,
    isComposerMode: mode === 'composer',
    isOnlineMode: mode === 'online',
  };

  return (
    <CanvasModeContext.Provider value={value}>
      {children}
    </CanvasModeContext.Provider>
  );
};

export const useCanvasMode = () => {
  const context = useContext(CanvasModeContext);
  if (!context) {
    throw new Error('useCanvasMode must be used within CanvasModeProvider');
  }
  return context;
};
